import React from "react";
import { GeoJSON, useMap } from "react-leaflet";
import apiClient from "../api/client";

const GLOW_LAYERS = [
  { weight: 5, color: "#3b6fd4", opacity: 0.18 },
  { weight: 2, color: "#5b8de8", opacity: 0.85 },
];

/**
 * Overlay to be mounted INSIDE an existing MapContainer.
 * No MapContainer here, no buttons, no raw JSON.
 */
export default function HeatmapOverlay({ enabled }) {
  const map = useMap();
  const [geom, setGeom] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [renderKey, setRenderKey] = React.useState(0);

  const loadHeatmap = React.useCallback(async () => {
    if (!enabled) return;

    try {
      setIsLoading(true);

      const bounds = map.getBounds();
      const bbox = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ].join(",");

      const res = await apiClient.get("/api/routes/heatmap/", { params: { bbox } });

      let data = res.data;
      if (typeof data === "string") data = JSON.parse(data);

      const emptyMultiLine =
        data?.type === "MultiLineString" &&
        Array.isArray(data.coordinates) &&
        data.coordinates.length === 0;

      const emptyFC =
        data?.type === "FeatureCollection" &&
        Array.isArray(data.features) &&
        data.features.length === 0;

      if (emptyMultiLine || emptyFC) {
        setGeom(null);
        return;
      }

      setGeom(data);
      setRenderKey((k) => k + 1);
    } catch (err) {
      console.error("Heatmap load failed:", err);
      setGeom(null);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, map]);

  // load immediately when enabled becomes true
  React.useEffect(() => {
    if (!enabled) {
      setGeom(null);
      setIsLoading(false);
      return;
    }
    loadHeatmap();
  }, [enabled, loadHeatmap]);

  // keep updated on pan/zoom (debounced)
  React.useEffect(() => {
    if (!enabled) return;

    let t = null;
    const schedule = () => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => loadHeatmap(), 250);
    };

    map.on("moveend", schedule);
    map.on("zoomend", schedule);

    return () => {
      if (t) window.clearTimeout(t);
      map.off("moveend", schedule);
      map.off("zoomend", schedule);
    };
  }, [enabled, map, loadHeatmap]);

  if (!enabled) return null;

  return (
    <>
      {isLoading && (
        <div className="pointer-events-none absolute left-4 bottom-4 z-[4000]">
          <div className="rounded-md border border-[#333] bg-black/80 px-3 py-2 text-[12px] text-white shadow">
            Loading heatmap&hellip;
          </div>
        </div>
      )}

      {geom &&
        GLOW_LAYERS.map((layer, i) => (
          <GeoJSON
            key={`${renderKey}-${i}`}
            data={geom}
            style={{
              color: layer.color,
              weight: layer.weight,
              opacity: layer.opacity,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        ))}
    </>
  );
}
